// Global Types
import type {
  $Children,
  $Component,
} from 'common.types';
import type {
  $Model,
  $ModelUpsertParams,
} from 'common.data';
import type {
  $Translate,
} from 'common.language';

// Global Components
import * as React from 'react';

// Helpers
import ErrorLog from '@vroskus/library-sentry/dist/browser';
import {
  animationClass,
} from '@vroskus/library-helpers/dist/browser';

// Components
import Loader from '../../layout/Loader';
import InputCheckbox from '../../form/InputCheckbox';

// Services
import Services from '../../../services';

// Types
type $Props = $Translate & {
  children: $Children;
  entryId: string;
  onSubmit: (data: Array<$ModelUpsertParams<'RegattaEntryCrewmember'>>) => unknown;
  regattaEntryId: string;
};

type $State = {
  data: Array<$Model<'RegattaEntryCrewmember'>>;
  loading: boolean;
  selected: Array<string>;
  visible: boolean;
};

class RegattaEntryCrewmembersDialog extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
      selected: [],
      visible: false,
    };
  }

  componentDidMount(): void {
    this.getData();
  }

  componentDidUpdate(prevProps: $Props): void {
    const {
      entryId,
    } = this.props;

    if (entryId !== prevProps.entryId) {
      this.getData();
    }
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      entryId,
    } = this.props;

    try {
      await this.setStateAsync({
        loading: true,
      });

      const data = await Services.Api.getAllRegattaEntryCrewmemberData({
        EntryId: entryId,
      });

      await this.setStateAsync({
        data,
        loading: false,
        selected: [],
      });
    } catch (error) {
      await this.setStateAsync({
        loading: false,
      });

      ErrorLog.exception(error as Error);
    }
  }

  toggle(): void {
    this.setState((prevState: $State) => ({
      visible: !prevState.visible,
    }));
  }

  childrenWithToggleHandler(): $Component<unknown> | null {
    const {
      children,
    } = this.props;

    if (children && React.isValidElement(children)) {
      return React.cloneElement(
        children,
        {
          // @ts-expect-error dialog
          onClick: () => this.toggle(),
        },
      );
    }

    return null;
  }

  submit(): void {
    const {
      onSubmit,
      regattaEntryId,
    } = this.props;
    const {
      data,
      selected,
    } = this.state;

    const preparedData: Array<$ModelUpsertParams<'RegattaEntryCrewmember'>> = data.filter((
      dataItem: $Model<'RegattaEntryCrewmember'>,
    ) => selected.includes(dataItem.id)).map((
      dataItem: $Model<'RegattaEntryCrewmember'>,
    ) => ({
      ...dataItem,
      id: undefined,
      RegattaEntryId: regattaEntryId,
    }));

    onSubmit(preparedData);

    this.setState({
      selected: [],
      visible: false,
    });
  }

  render() {
    const {
      t,
    } = this.props;
    const {
      data,
      loading,
      selected,
      visible,
    } = this.state;

    return (
      <>
        {this.childrenWithToggleHandler()}
        <div className={`modal bg-gray-300 bg-opacity-50 ${visible ? 'active' : ''} ${animationClass('fadeIn')}`}>
          <div className={'modal-dialog modal-dialog_centered max-w-sm mx-auto'}>
            <div className={'modal-content w-full'}>
              <div className={'modal-header'}>
                <h3 className={'h3'}>
                  {t('Previous crewmembers')}
                </h3>
                <button
                  className={'close la la-times'}
                  onClick={() => this.toggle()}
                  type={'button'}
                />
              </div>
              <div className={'modal-body max-h-80 overflow-y-auto'}>
                <Loader loading={loading === true}>
                  {data.map((dataItem: $Model<'RegattaEntryCrewmember'>) => (
                    <div key={dataItem.id}>
                      <InputCheckbox
                        label={dataItem.Name}
                        name={'_regattaEntryCrewmember'}
                        onChange={(state) => {
                          let updatedSelected = selected;

                          if (state === true) {
                            updatedSelected.push(dataItem.id);
                          } else {
                            updatedSelected = updatedSelected.filter((
                              id: string,
                            ) => id !== dataItem.id);
                          }

                          this.setState({
                            selected: updatedSelected,
                          });
                        }}
                        value={selected.includes(dataItem.id)}
                      />
                    </div>
                  ))}
                </Loader>
              </div>
              <div className={'modal-footer flex justify-end'}>
                <button
                  className={'btn btn_success btn_outlined uppercase mr-3'}
                  onClick={() => this.submit()}
                  type={'button'}
                >
                  {t('Add')}
                </button>
                <button
                  className={'btn btn_secondary btn_outlined uppercase'}
                  onClick={() => this.toggle()}
                  type={'button'}
                >
                  {t('Close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }
}

export default RegattaEntryCrewmembersDialog;
